/** Shared payroll calculation: CTC + attendance-linked absences. */

export const ROLE_DEFAULT_CTC = {
  doctor: 120000,
  nurse: 28000,
  billing: 22000,
  reception: 20000,
  pharmacy: 25000,
  lab: 26000,
  manager: 80000,
  hr: 35000,
  ipd: 22000,
  housekeeping: 18000,
};

const WORKING_DAYS = 26;

export function resolveCtc(staff) {
  const raw = Number(staff?.ctc ?? staff?.salary ?? staff?.basicSalary);
  if (Number.isFinite(raw) && raw > 0) return Math.round(raw);
  return ROLE_DEFAULT_CTC[staff?.role] || 20000;
}

/** Count Absent / Leave / Half-day (0.5) from attendance records for one staff. */
export function countAbsences(records, staffId) {
  if (!staffId || !Array.isArray(records)) return 0;
  let days = 0;
  for (const r of records) {
    if (String(r.staffId) !== String(staffId)) continue;
    const st = String(r.status || '').toLowerCase();
    if (st === 'absent' || st === 'leave' || st === 'on leave' || st === 'unpaid leave') days += 1;
    else if (st === 'half-day' || st === 'half day' || st === 'halfday') days += 0.5;
  }
  return days;
}

export function buildPayrollLine(staff, absentDays = 0) {
  const base = resolveCtc(staff);
  const absent = Math.max(0, Number(absentDays) || 0);
  const pfWage = Math.min(base, 15000);
  const pf = Math.round(pfWage * 0.12);
  const hra = Math.round(base * 0.4);
  const da = Math.round(base * 0.1);
  const ta = Math.round(base * 0.05);
  const special = staff?.role === 'doctor' ? 15000 : 0;
  const gross = base + hra + da + ta + special;
  // ESI on gross wages when ≤ ₹21,000
  const esi = gross <= 21000 ? Math.round(gross * 0.0075) : 0;
  const esiEmployer = gross <= 21000 ? Math.round(gross * 0.0325) : 0;
  const pfEmployer = Math.round(pfWage * 0.12);
  const pt = 200;
  const tds = staff?.role === 'doctor' ? Math.round(base * 0.1) : 0;
  const loans = Number(staff?.loanDeduction) || 0;
  const absentDeduction = Math.round((base / WORKING_DAYS) * absent);
  const totalDeductions = pf + esi + pt + tds + loans + absentDeduction;
  const net = Math.max(0, gross - totalDeductions);
  const staffId = staff.staffId || staff.uid || staff.id || staff.email;
  return {
    id: staffId,
    staffId,
    name: staff.name || '—',
    dept: staff.roleLabel || staff.department || staff.role || '—',
    designation: staff.designation || staff.title || staff.specialty || staff.role || '—',
    role: staff.role || '',
    ctc: base,
    ctcSource: Number(staff.ctc) > 0 ? 'staff' : 'role-default',
    basic: base,
    hra,
    ta,
    da,
    special,
    pf,
    pfEmployer,
    esi,
    esiEmployer,
    pt,
    tds,
    loans,
    absent,
    gross,
    absentDeduction,
    totalDeductions,
    net,
  };
}

export function linesFromSavedPayroll(savedLines) {
  return (savedLines || []).map((l) => {
    const basic = Number(l.base ?? l.basic ?? 0);
    const hra = Number(l.hra || 0);
    const da = Number(l.da || 0);
    const ta = Number(l.ta || 0);
    const special = Number(l.special || 0);
    const pf = Number(l.pf || 0);
    const esi = Number(l.esi || 0);
    const pt = Number(l.pt || 0);
    const tds = Number(l.tds || 0);
    const loans = Number(l.loans || 0);
    const absent = Number(l.absent || 0);
    const absentDeduction = Number(l.absentDeduction ?? Math.round((basic / WORKING_DAYS) * absent));
    const gross = Number(l.gross ?? basic + hra + da + ta + special);
    const totalDeductions = Number(l.totalDeductions ?? pf + esi + pt + tds + loans + absentDeduction);
    const net = Number(l.net ?? Math.max(0, gross - totalDeductions));
    const staffId = l.staffId || l.id;
    return {
      id: staffId,
      staffId,
      name: l.name,
      dept: l.dept || l.role,
      designation: l.designation || l.role,
      role: l.role,
      ctc: basic,
      ctcSource: l.ctcSource || 'saved',
      basic,
      hra,
      ta,
      da,
      special,
      pf,
      esi,
      pt,
      tds,
      loans,
      absent,
      gross,
      absentDeduction,
      totalDeductions,
      net,
    };
  });
}

export function toApiPayrollLines(staffLines) {
  return (staffLines || []).map((e) => ({
    staffId: e.staffId || e.id,
    name: e.name,
    role: e.role || e.dept,
    base: e.basic,
    hra: e.hra,
    da: e.da,
    ta: e.ta || 0,
    special: e.special || 0,
    pf: e.pf,
    esi: e.esi || 0,
    pt: e.pt || 0,
    tds: e.tds,
    loans: e.loans || 0,
    absent: e.absent || 0,
    absentDeduction: e.absentDeduction || 0,
    gross: e.gross,
    totalDeductions: e.totalDeductions,
    net: e.net,
    ctcSource: e.ctcSource,
  }));
}
